/**
 * ForensicInsightsCard - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows forensic analysis results including:
 * - Benford's Law analysis
 * - Statistical outlier detection
 * - Data integrity score
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Shield,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';

interface ForensicInsight {
  type: 'benford' | 'outlier' | 'integrity' | 'pattern';
  title: string;
  finding: string;
  severity: 'normal' | 'warning' | 'anomaly';
  score?: number;
  details?: Record<string, any>;
}

interface ForensicInsightsCardProps {
  reportId: string;
  className?: string;
  /**
   * Optional pre-fetched slice from the batched /dashboard/case-details/{id}
   * endpoint. When provided, the widget skips its own fetch and renders from
   * this slice. Back-compat: passing only `reportId` keeps the original
   * per-widget fetch behaviour (still used by AttorneyQueue).
   */
  data?: { insights?: ForensicInsight[]; error?: string } | null;
  isLoading?: boolean;
}

const INSIGHT_ICONS = {
  benford: BarChart3,
  outlier: TrendingUp,
  integrity: Shield,
  pattern: Search
};

const SEVERITY_STYLES = {
  normal: {
    bg: { backgroundColor: 'transparent' },
    border: { borderColor: 'var(--color-border)' },
    icon: { color: 'var(--color-verified)' },
    badge: { backgroundColor: 'transparent', color: 'var(--color-verified)' }
  },
  warning: {
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    border: { borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: { color: 'var(--color-brand)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
  },
  anomaly: {
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    border: { borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: { color: 'var(--color-brand)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
  }
};

export default function ForensicInsightsCard({
  reportId,
  className = '',
  data: sliceData,
  isLoading: externalLoading,
}: ForensicInsightsCardProps) {
  const { getToken } = useAuth();
  const [insights, setInsights] = useState<ForensicInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Batched mode: parent passed a slice from /dashboard/case-details.
  // We skip the per-widget fetch entirely and render from the slice.
  const batchedMode = sliceData !== undefined;

  useEffect(() => {
    if (batchedMode) return; // parent owns the fetch
    if (reportId) fetchInsights();
  }, [reportId, batchedMode]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/forensic-insights/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setInsights(data.insights || []);
    } catch (err) {
      console.debug('Forensic insights API not available');
      setInsights([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // In batched mode, derive render state from the slice.
  const effectiveInsights: ForensicInsight[] = batchedMode
    ? (sliceData?.insights || [])
    : insights;
  const effectiveLoading = batchedMode ? !!externalLoading : loading;
  const effectiveError = batchedMode
    ? !!(sliceData && 'error' in (sliceData || {}) && sliceData.error)
    : error;

  const overallStatus = effectiveInsights.some(i => i.severity === 'anomaly') ? 'anomaly' :
                        effectiveInsights.some(i => i.severity === 'warning') ? 'warning' : 'normal';

  if (effectiveLoading) {
    return (
      <div className={`rounded-md border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-md border ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Search className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
            <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Forensic Insights</h3>
          </div>
          {effectiveError && effectiveInsights.length === 0 ? (
            /* Don't claim "All Clear" when insights failed to load — the body
               shows "unavailable", so the badge must not contradict it. */
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
              Unavailable
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={SEVERITY_STYLES[overallStatus].badge}>
              {overallStatus === 'normal' ? 'All Clear' :
               overallStatus === 'warning' ? 'Review Needed' : 'Issues Found'}
            </span>
          )}
        </div>
      </div>

      {/* Insights list */}
      <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
        {effectiveError && effectiveInsights.length === 0 ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Forensic insights unavailable</p>
            {!batchedMode && (
              <button
                onClick={fetchInsights}
                className="mt-2 text-xs"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Retry
              </button>
            )}
          </div>
        ) : effectiveInsights.length === 0 ? (
          <div className="p-6 text-center">
            <Search className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No forensic insights yet</p>
          </div>
        ) : effectiveInsights.map((insight, index) => {
          const Icon = INSIGHT_ICONS[insight.type] || Search;
          const styles = SEVERITY_STYLES[insight.severity];
          const isExpanded = expanded === insight.type;

          return (
            <div key={index} className="p-4" style={styles.bg}>
              <button
                onClick={() => setExpanded(isExpanded ? null : insight.type)}
                className="w-full text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className="p-2 rounded-md mr-3" style={{ backgroundColor: 'var(--color-bg-surface)', ...styles.icon }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{insight.title}</h4>
                      <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{insight.finding}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {insight.score !== undefined && (
                      <span className="text-lg font-bold mr-2 font-mono tabular-nums" style={styles.icon}>
                        {insight.type === 'integrity' ? `${insight.score}%` : insight.score}
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                    ) : (
                      <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded details */}
              {isExpanded && insight.details && (
                <div className="mt-4 p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <h5 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Details</h5>
                  <pre className="text-xs overflow-x-auto" style={{ color: 'var(--color-text-secondary)' }}>
                    {JSON.stringify(insight.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-4 border-t rounded-b-md" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
        <p className="text-xs flex items-center" style={{ color: 'var(--color-text-muted)' }}>
          <Info className="w-4 h-4 mr-1" />
          Forensic analysis helps identify potential data anomalies that may require explanation.
        </p>
      </div>
    </div>
  );
}
